import { faStar as farStar } from '@fortawesome/pro-regular-svg-icons';
import { faStar as fasStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import { STAR, UNSTAR } from 'mk/autogenerated/translations/Star.5acf3a7da1efa1c8c2099207bfee5668'
import { LoadingPlaceholder } from 'mk2/components/LoadingPlaceholder';
import { getStarrable } from 'mk2/containers/Star/Star.selectors';
import { AppState } from 'mk2/reducers';
import React from 'react';
import { connect } from 'react-redux';
import { starToggle } from './Star.actions';
import styles from './Star.mscss';

interface OwnProps {
    className?: string;
    isStarred: boolean;
    showCount?: boolean;
    starsCount?: number;
    enableKeyboardShortcut?: boolean;

    children?(props: {
        isStarred: boolean;
        count: number;
        onStarToggle(event: React.MouseEvent<HTMLElement>): void;
    }): JSX.Element;
    onStarToggle();
}

export class Star extends React.PureComponent<OwnProps> {
    public static defaultProps = {
        enableKeyboardShortcut: false,
        starsCount: 0,
    };

    public componentDidMount() {
        if (this.props.enableKeyboardShortcut) {
            document.addEventListener('keyup', this.handleOnKeyPressed);
        }
    }

    public componentDidUpdate(prevProps: Readonly<OwnProps>): void {
        if (prevProps.enableKeyboardShortcut && !this.props.enableKeyboardShortcut) {
            document.removeEventListener('keyup', this.handleOnKeyPressed);
        } else if (!prevProps.enableKeyboardShortcut && this.props.enableKeyboardShortcut) {
            document.addEventListener('keyup', this.handleOnKeyPressed);
        }
    }

    public componentWillUnmount() {
        if (this.props.enableKeyboardShortcut) {
            document.removeEventListener('keyup', this.handleOnKeyPressed);
        }
    }

    public render() {
        const { className, isStarred, starsCount, showCount, children } = this.props;

        if (children) {
            // Render prop pattern - the most customizable
            return children({
                isStarred,
                count: starsCount,
                onStarToggle: this.onStarToggle,
            });
        }

        return (
            <div
                className={cx(
                    styles.Star,
                    className,
                    isStarred && styles['Star--isStarred'],
                    showCount && styles['Star--withCount'],
                    showCount && starsCount > 0 && styles['Star--nonZero'],
                )}
                onClick={this.onStarToggle}
                aria-label={isStarred ? UNSTAR : STAR}
                title={isStarred ? UNSTAR : STAR}
            >
                <FontAwesomeIcon icon={isStarred ? fasStar : farStar} />
                {showCount && (
                    <span className={cx(styles.Star__count, starsCount === 0 && styles['Star__count--zero'])}>
                        {starsCount}
                    </span>
                )}
            </div>
        );
    }

    private onStarToggle = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        this.props.onStarToggle();
    };

    private handleOnKeyPressed = (ev: KeyboardEvent) => {
        const { onStarToggle } = this.props;

        // Ignore inputs
        if (['input', 'textarea', 'select'].indexOf((ev.target as HTMLElement).tagName.toLowerCase()) !== -1) {
            return;
        }

        if (ev.metaKey || ev.altKey || ev.ctrlKey) {
            return;
        }

        if (ev.which === 83) {
            // S
            ev.stopPropagation();
            ev.preventDefault();
            onStarToggle();
        }
    };
}

interface ConnectedStarOwnProps {
    className?: string;
    entity: string;
    id: number;
    showCount?: boolean;
    enableKeyboardShortcut?: boolean;

    children?(props: {
        isStarred: boolean;
        count: number;
        onStarToggle(event: React.MouseEvent<HTMLElement>): void;
    }): JSX.Element;
}

interface ConnectedStarStateProps {
    isStarred: boolean;
    starsCount: number;
}

interface ConnectedStarDispatchProps {
    onStarToggle();
}

type ConnectedStarProps = ConnectedStarOwnProps & ConnectedStarStateProps & ConnectedStarDispatchProps;

export const ConnectedStarComponent: React.StatelessComponent<ConnectedStarProps> = ({
    className, isStarred, starsCount, showCount, enableKeyboardShortcut, children, onStarToggle,
}) => (
    <Star
        className={className}
        isStarred={isStarred}
        showCount={showCount}
        starsCount={starsCount}
        enableKeyboardShortcut={enableKeyboardShortcut}
        children={children}
        onStarToggle={onStarToggle}
    />
);

function mapStateToProps(state: AppState, ownProps: ConnectedStarOwnProps): ConnectedStarStateProps {
    const starrable = getStarrable(state, ownProps.entity, ownProps.id);
    return {
        isStarred: starrable.isStarred,
        starsCount: starrable.starsCount,
    };
}

function mapDispatchToProps(dispatch, ownProps: ConnectedStarOwnProps): ConnectedStarDispatchProps {
    return {
        onStarToggle: () => dispatch(starToggle(ownProps.entity, ownProps.id)),
    };
}

export const ConnectedStar = connect(mapStateToProps, mapDispatchToProps)(ConnectedStarComponent);

interface PlaceholderOwnProps {
    className?: string;
    showCount?: boolean;
}

export const StarPlaceholder: React.FunctionComponent<PlaceholderOwnProps> = ({ className, showCount = false }) => (
    <div className={cx(styles.Star, className, showCount && styles['Star--withCount'])} aria-label={STAR} title={STAR}>
        <FontAwesomeIcon icon={farStar} />
        {showCount && (
            <span className={cx(styles.Star__count, styles['Star__count--zero'])}>
                <LoadingPlaceholder width={10} />
            </span>
        )}
    </div>
);

StarPlaceholder.displayName = 'StarPlaceholder';
